/**
 * The receiving end of a plan link (`D273`): `<base>?plan=<id>` opens the
 * site, and this keeps the id until the person can see the plan.
 *
 * ── READ ONCE, BEFORE THE ROUTER, AND THEN GONE FROM THE URL ───────────────
 * {@link capturePlanLanding} runs in `main.tsx`'s boot beside
 * `captureShareLanding` (`D260`), which handles the link's `ref`. It keeps
 * the id on the device and takes `plan` out of the address. Nothing is read
 * from or written to the server here, and nothing opens.
 *
 * ── OPENED AFTER THE GATE, NEVER BEFORE (`D262`) ───────────────────────────
 * A person without an account meets the intake and the e-mail first. Once
 * the intake commits, its hand-off takes the kept id and lands on the
 * preview INSTEAD of the review that generates (`T-145`); a person already
 * signed in meets it through `PendingSharedPlanDoor`, the first time the app
 * shows no sheet. Either way the id is taken once and forgotten.
 *
 * The native shell never boots with `?plan=` (its deep links carry two keys,
 * `D96`, `T-178`), so on the phone this finds nothing; the links land on the
 * site until App Links are declared at launch.
 */
import type { PlanShareId } from "@repo/contract/community";
import { preferences } from "../../native";
import { landingPlan, livePendingPlan, withoutPlan } from "./model";

export const PENDING_PLAN_KEY = "mypocketgym.share.pending-plan";

export function capturePlanLanding(
  location: Pick<Location, "hash" | "pathname" | "search"> = window.location,
  history: Pick<History, "replaceState" | "state"> = window.history,
  now: () => number = Date.now
): void {
  const shareId = landingPlan(location.search);
  if (shareId === null) {
    return;
  }
  history.replaceState(
    history.state,
    "",
    `${location.pathname}${withoutPlan(location.search)}${location.hash}`
  );
  preferences
    .set(PENDING_PLAN_KEY, JSON.stringify({ at: now(), shareId }))
    .catch(() => undefined);
}

/** The kept id, if live, and forgotten in the same breath: taken once. */
export async function takePendingPlan(
  now: number = Date.now()
): Promise<PlanShareId | null> {
  const raw = await preferences.get(PENDING_PLAN_KEY).catch(() => null);
  if (raw === null) {
    return null;
  }
  await preferences.remove(PENDING_PLAN_KEY).catch(() => undefined);
  return livePendingPlan(raw, now);
}
